import React, { useState } from "react";
import { useQuery, useAction } from "convex/react";
import { api } from "../../lib/convexApi";
import { SUPPORTED_AGENTS } from "../../api/mcp";
import { MintMcpTokenModal } from "./MintMcpTokenModal";
import { AgentInstallerRow } from "./AgentInstallerRow";
import { ConnectionHealthPanel } from "./ConnectionHealthPanel";

// Token row shape returned by api.mcp.tokens.queries.list — kept loose
// for the same reason MemoryPage uses (api as any): the runtime Proxy in
// convex/_generated/api.ts gives us `any` for types until `npx convex
// dev` regenerates real types.
interface TokenRow {
  _id: string;
  label: string;
  createdBy: string;
  createdAt: number;
  lastUsedAt?: number | null;
}

interface MintResult {
  rawToken: string;
  record: {
    id: string;
    label: string;
    createdBy: string;
    createdAt: number;
  };
}

export function ConnectAgentsTab() {
  // Per [[feedback-pr46-review-patterns]] #5, ConnectAgentsTab consumes
  // the actual Convex return shapes — no stale-typed wrapper between the
  // hook and the table render.
  const tokens = useQuery((api as any).mcp.tokens.queries.list, {}) as TokenRow[] | undefined;
  const mintToken = useAction((api as any).mcp.tokens.actions.mintToken) as unknown as (args: {
    label: string;
  }) => Promise<MintResult>;
  const revokeToken = useAction((api as any).mcp.tokens.actions.revokeToken) as unknown as (args: {
    tokenId: string;
  }) => Promise<{ ok: boolean }>;

  const [mintLabel, setMintLabel] = useState("");
  const [minting, setMinting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mintedToken, setMintedToken] = useState<{
    rawToken: string;
    label: string;
  } | null>(null);

  async function handleMint() {
    const label = mintLabel.trim();
    if (!label) {
      setError("Label is required");
      return;
    }
    setMinting(true);
    setError(null);
    try {
      const r = await mintToken({ label });
      setMintedToken({ rawToken: r.rawToken, label: r.record.label });
      setMintLabel("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to mint token");
    } finally {
      setMinting(false);
    }
  }

  async function handleRevoke(token: TokenRow) {
    // Revoke confirm uses window.confirm to match the pattern from the
    // pre-cutover surface; PR 8 may upgrade to a proper modal once the
    // policy controls / approvals UX settles.
    if (!window.confirm(`Revoke "${token.label}"? Any agent using it will be cut off.`)) {
      return;
    }
    try {
      await revokeToken({ tokenId: token._id });
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to revoke ${token.label}`);
    }
  }

  const loading = tokens === undefined;
  const tokenList = tokens ?? [];
  const hasToken = tokenList.length > 0;

  return (
    <div className="ac-vault-tab">
      <div className="ac-tab-header">
        <div>
          <h2 className="ac-tab-title">Connect agents</h2>
          <p className="ac-tab-subtitle">
            Mint bearer tokens for the Steerly memory MCP and install it into agents you use. Tokens
            are shown once at mint time; lose them and you'll need to re-mint.
          </p>
        </div>
      </div>

      {error && <p className="ac-error-banner">{error}</p>}

      <section className="ac-tab-section" aria-label="MCP tokens">
        <h3>MCP tokens</h3>
        <div className="ac-mint-row">
          <input
            type="text"
            value={mintLabel}
            onChange={(e) => setMintLabel(e.target.value)}
            placeholder="Label (e.g., 'laptop')"
            aria-label="Token label"
            disabled={minting}
          />
          <button
            className="ac-btn ac-btn--primary"
            onClick={() => void handleMint()}
            disabled={minting || !mintLabel.trim()}
          >
            {minting ? "Minting…" : "Mint personal MCP token"}
          </button>
        </div>
        {loading ? (
          <p className="ac-loading">Loading tokens…</p>
        ) : tokenList.length === 0 ? (
          <div className="ac-empty-state">
            <p>No MCP tokens minted yet. Mint one above to install Steerly into an agent.</p>
          </div>
        ) : (
          <table className="ac-vault-table" aria-label="MCP tokens">
            <thead>
              <tr>
                <th>Label</th>
                <th>Created by</th>
                <th>Created</th>
                <th>Last used</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tokenList.map((token) => (
                <tr key={token._id} className="ac-vault-row">
                  <td className="ac-vault-row__name">{token.label}</td>
                  <td>{token.createdBy}</td>
                  <td>{new Date(token.createdAt).toLocaleString()}</td>
                  <td>{token.lastUsedAt ? new Date(token.lastUsedAt).toLocaleString() : "—"}</td>
                  <td>
                    <button
                      className="ac-btn ac-btn--danger ac-btn--sm"
                      onClick={() => void handleRevoke(token)}
                      aria-label={`Revoke ${token.label}`}
                    >
                      Revoke
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="ac-tab-section" aria-label="Agent installers">
        <h3>Agent installers</h3>
        <p className="ac-tab-subtitle">
          Install Steerly's memory MCP into agents on this machine. Uninstall requires a reason for
          the audit log.
        </p>
        <table className="ac-vault-table" aria-label="Supported agents">
          <thead>
            <tr>
              <th>Agent</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {SUPPORTED_AGENTS.map((agent) => (
              <AgentInstallerRow
                key={agent.value}
                agent={agent.value}
                label={agent.label}
                hasToken={hasToken}
              />
            ))}
          </tbody>
        </table>
      </section>

      <ConnectionHealthPanel />

      {mintedToken && (
        <MintMcpTokenModal
          rawToken={mintedToken.rawToken}
          label={mintedToken.label}
          onClose={() => setMintedToken(null)}
        />
      )}
    </div>
  );
}
